import React, { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom"; // Import useNavigate hook
import "./RegisterBusiness.css";
import { API_URL } from "../../services/api";

const RegisterBusiness = () => {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    email: "",
    website: "",
    business_type: "eco_friendly_company", // Always eco-friendly business
    shipping_strategy: "",
    price_range: "",
    production: "",
  });

  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [isIndividual, setIsIndividual] = useState(false); // Track if individual seller is selected
  const [loading, setLoading] = useState(false); // Track loading state

  const navigate = useNavigate(); // Initialize navigate

  // Handle form field changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  // Handle business type change
  const handleBusinessTypeChange = (e) => {
    const { value } = e.target;
    setIsIndividual(value === "individual_seller");
    setFormData((prevData) => ({
      ...prevData,
      business_type: "eco_friendly_company", // Keep this value fixed
      description:
        value === "individual_seller"
          ? "* This is an individual seller, not verified by Verdure."
          : prevData.description,
    }));
  };

  // Handle file change for logo
  const handleFileChange = (e) => {
    const { name, files } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: files[0], // Save the file as part of the form data
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); // Set loading to true when the form is being submitted

    try {
      // Create a FormData object to handle both file and text fields
      const formDataObj = new FormData();
      for (let key in formData) {
        if (formData.hasOwnProperty(key)) {
          // Append each form field to the FormData object
          formDataObj.append(key, formData[key]);
        }
      }

      // If the logo is included in the form data, append it as well
      const logoInput = document.getElementById("logo");
      if (logoInput.files.length > 0) {
        formDataObj.append("logo", logoInput.files[0]);
      }

      const bannerInput = document.getElementById("banner");
      // console.log(bannerInput.files);
      if (bannerInput.files.length > 0) {
        formDataObj.append("banner", bannerInput.files[0]);
      }

      // Get the auth token from localStorage
      const token = localStorage.getItem("access_token");

      // Submit the form data to the backend
      const response = await axios.post(
        `${API_URL}api/registerbiz/`,
        formDataObj,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data", // Important for file uploads
          },
        }
      );

      if (response.data.stripe_onboarding_url) {
        // Redirect to Stripe's onboarding page if available
        window.location.href = response.data.stripe_onboarding_url;
      }

      setSuccess(true);
      setError(""); // Clear any previous errors
      setLoading(false); // Reset loading state after successful submission
    } catch (err) {
      setError("Failed to register business.");
      setSuccess(false);
      setLoading(false); // Reset loading state on error
      console.error("Error:", err);
      // window.location.href = '/logout/';
    }
  };

  return (
    <div class="regbus">
      <h2 class="headingtitle">Register Your Business</h2>
      <p>
        <b>
          After filling out this form, you will be taken to Stripe to create an
          account with the email you registered with.{" "}
        </b>
      </p>
      <p>
        <b>
          This part is important because it lets you configure payouts securely.
          It will ask for you to set up a password, along with putting your
          details in.
        </b>
      </p>
      <p>
        <b>This is a once and done process!</b>
      </p>
      <p>
        <b>
          We are working on integrations with Quickbooks and other accounting
          software for your Stripe Dashboard as well.
        </b>
      </p>
      {success && (
        <p>
          Business registered successfully! Taking you to Stripe, please do not
          reload...
        </p>
      )}
      {error && <p style={{ color: "red" }}>{error}</p>}
      <div class="regbusform">
        <form onSubmit={handleSubmit}>
          <div className="regbus-form regbus-form-name">
            <label htmlFor="name">Business Name</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
            />
          </div>

          {/* Business Type dropdown */}
          <div className="regbus-form regbus-form-bustype">
            <label htmlFor="business_type">
              Select Business Type (If business, select Business)
            </label>
            <select
              id="business_type"
              name="business_type"
              onChange={handleBusinessTypeChange}
              value={
                isIndividual ? "individual_seller" : "eco_friendly_company"
              }
            >
              <option value="eco_friendly_company">
                Eco-Friendly Business
              </option>
              <option value="individual_seller">Individual Seller</option>
            </select>
          </div>

          {/* Conditionally render description and website fields based on the selection */}
          {!isIndividual && (
            <>
              <div className="regbus-form regbus-form-description">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  required
                ></textarea>
              </div>

              <div className="regbus-form regbus-form-website">
                <label htmlFor="website">
                  Website (Please Append https://)
                </label>
                <input
                  type="url"
                  id="website"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                />
              </div>
            </>
          )}

          <div className="regbus-form regbus-form-email">
            <label htmlFor="email">Public Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
            />
          </div>

          {/* New Fields */}
          <div className="regbus-form regbus-form-ship">
            <label htmlFor="shipping_strategy">Shipping Strategy</label>
            <textarea
              id="shipping_strategy"
              name="shipping_strategy"
              value={formData.shipping_strategy}
              onChange={handleChange}
              placeholder="Describe how you handle shipping"
              required
            ></textarea>
          </div>

          <div className="regbus-form regbus-form-price">
            <label htmlFor="price_range">Price Range</label>
            <textarea
              id="price_range"
              name="price_range"
              value={formData.price_range}
              onChange={handleChange}
              placeholder="Provide your business's price range"
              required
            ></textarea>
          </div>

          <div className="regbus-form regbus-form-production">
            <label htmlFor="production">
              Production (Eco-friendly Practices)
            </label>
            <textarea
              id="production"
              name="production"
              value={formData.production}
              onChange={handleChange}
              placeholder="Explain your eco-friendly production methods"
              required
            ></textarea>
          </div>

          <div className="regbus-form regbus-form-logo">
            <label htmlFor="logo">Business Logo</label>
            <input
              type="file"
              id="logo"
              name="logo"
              onChange={handleFileChange}
              accept="image/*"
            />
          </div>

          <div className="regbus-form regbus-form-logo">
            <label htmlFor="banner">Business Banner</label>
            <input
              type="file"
              id="banner"
              name="banner"
              onChange={handleFileChange}
              accept="image/*"
            />
          </div>
          <div class="regbus-form">
            {/* <button class="signIn-but" disabled={loading}>
            {loading ? (
              <div className=""></div>
            ) : (
              "Register Business"
            )}
          </button> */}
            <button className="signIn-but" disabled={loading}>
              {loading ? "Processing..." : "Register Business"}
            </button>
          </div>
        </form>
      </div>
      {loading && (
        <div className="loading-overlay">
          <div className="spinner"></div>
        </div>
      )}
    </div>
  );
};

export default RegisterBusiness;
